import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, ArrowLeft, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import IssueDetailDialog from "@/components/IssueDetailDialog";

interface Issue {
  id: string;
  title: string;
  description: string;
  issue_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  profile_id: string;
  profiles?: { full_name: string | null } | null;
  _comment_count?: number;
}

interface IssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ISSUE_TYPES = [
  { value: "bug", label: "Bug" },
  { value: "feature_request", label: "Feature Request" },
  { value: "data_syncing", label: "Data Syncing" },
  { value: "custom_help", label: "Custom Help" },
  { value: "other", label: "Other" },
];

const statusColor: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 border-blue-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

const typeColor: Record<string, string> = {
  bug: "bg-red-50 text-red-700 border-red-200",
  feature_request: "bg-purple-50 text-purple-700 border-purple-200",
  data_syncing: "bg-amber-50 text-amber-700 border-amber-200",
  custom_help: "bg-cyan-50 text-cyan-700 border-cyan-200",
  other: "bg-gray-50 text-gray-600 border-gray-200",
};

const IssueDialog = ({ open, onOpenChange }: IssueDialogProps) => {
  const { profile } = useAuth();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<"list" | "create">("list");

  // Create form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [issueType, setIssueType] = useState("bug");
  const [submitting, setSubmitting] = useState(false);

  // Detail view
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchIssues = async () => {
    if (!profile) return;
    setLoading(true);
    const { data } = await supabase
      .from("issues" as any)
      .select("id, title, description, issue_type, status, created_at, updated_at, profile_id, profiles(full_name)")
      .eq("profile_id", profile.id)
      .order("created_at", { ascending: false });

    const issuesData = (data as any[]) || [];

    // Fetch comment counts
    if (issuesData.length > 0) {
      const ids = issuesData.map((i: any) => i.id);
      const { data: commentCounts } = await supabase
        .from("issue_comments" as any)
        .select("issue_id")
        .in("issue_id", ids);
      const countMap = new Map<string, number>();
      (commentCounts || []).forEach((c: any) => {
        countMap.set(c.issue_id, (countMap.get(c.issue_id) || 0) + 1);
      });
      issuesData.forEach((issue: any) => {
        issue._comment_count = countMap.get(issue.id) || 0;
      });
    }

    setIssues(issuesData);
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchIssues();
      setView("list");
    }
  }, [open, profile]);

  const handleCreate = async () => {
    if (!title.trim() || !profile) return;
    setSubmitting(true);
    const { error } = await supabase.from("issues" as any).insert({
      title: title.trim(),
      description,
      issue_type: issueType,
      status: "open",
      profile_id: profile.id,
      client_id: profile.client_id,
    } as any);
    if (error) {
      toast.error("Failed to create issue");
    } else {
      toast.success("Issue reported successfully");
      setTitle("");
      setDescription("");
      setIssueType("bug");
      setView("list");
      fetchIssues();
    }
    setSubmitting(false);
  };

  const handleOpenDetail = (issue: Issue) => {
    setSelectedIssue(issue);
    setDetailOpen(true);
  };

  const handleStatusChange = (issueId: string, newStatus: string) => {
    setIssues((prev) =>
      prev.map((i) => (i.id === issueId ? { ...i, status: newStatus } : i))
    );
    if (selectedIssue?.id === issueId) {
      setSelectedIssue((prev) => prev ? { ...prev, status: newStatus } : prev);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              {view === "create" ? (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setView("list")}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <DialogTitle>Report New Issue</DialogTitle>
                </div>
              ) : (
                <DialogTitle>My Issues</DialogTitle>
              )}
              {view === "list" && (
                <Button size="sm" onClick={() => setView("create")}>
                  <Plus className="mr-1 h-4 w-4" /> New Issue
                </Button>
              )}
            </div>
          </DialogHeader>

          {view === "list" ? (
            <div className="flex-1 overflow-y-auto space-y-2">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : issues.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-8">
                  <p className="text-sm text-muted-foreground">No issues reported yet.</p>
                  <Button size="sm" variant="outline" onClick={() => setView("create")}>
                    Report your first issue
                  </Button>
                </div>
              ) : (
                issues.map((issue) => (
                  <button
                    key={issue.id}
                    onClick={() => handleOpenDetail(issue)}
                    className="w-full rounded-lg border border-border p-3 text-left transition-colors hover:bg-accent"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-tight truncate">{issue.title}</p>
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${typeColor[issue.issue_type] || ""}`}>
                            {ISSUE_TYPES.find((t) => t.value === issue.issue_type)?.label || issue.issue_type}
                          </Badge>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusColor[issue.status] || ""}`}>
                            {issue.status}
                          </Badge>
                          {(issue._comment_count || 0) > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                              <MessageSquare className="h-3 w-3" />
                              {issue._comment_count}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {format(new Date(issue.created_at), "MMM d")}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="issue-title">Title</Label>
                <Input
                  id="issue-title"
                  placeholder="Brief summary of the issue"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={issueType} onValueChange={setIssueType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Description</Label>
                <RichTextEditor
                  content={description}
                  onChange={setDescription}
                  placeholder="Describe the issue in detail. You can add images."
                />
              </div>

              <Button
                className="w-full"
                disabled={!title.trim() || submitting}
                onClick={handleCreate}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  "Submit Issue"
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <IssueDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        issue={selectedIssue}
        onBack={() => setDetailOpen(false)}
        onStatusChange={handleStatusChange}
      />
    </>
  );
};

export default IssueDialog;
